import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Upload,
  Input,
  Row,
  Col
} from 'antd';
import {
  UploadOutlined,
  CheckOutlined
} from '@ant-design/icons';
import type { FormInstance } from 'antd/lib/form/Form';
import DF from 'DisplayFramework';
import HeroTitle from 'components/theme_components/HeroTitle';
import StatCard from 'components/theme_components/StatCard';
import ButtonLink from 'components/theme_components/ButtonLink';
import Notifications from 'components/theme_components/Notifications';
import { bulkUploadUtils, downloadBulkUploadTemplate, processBulkUpload } from 'services/BulkUpload';

interface BulkUploadTabProps {
  form: FormInstance;
  color?: string;
  tpauthid: number;
  itemsCompanyHierarchyTree: any[];
  isActive?: boolean;
  onTabActivated?: () => void;
}

const BulkUploadTab: React.FC<BulkUploadTabProps> = ({ 
  form, 
  color,
  tpauthid, 
  itemsCompanyHierarchyTree, 
  isActive, 
  onTabActivated 
}) => {
  const [uploadForm] = Form.useForm();
  const [fileName, setFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  useEffect(() => {
    if (isActive) {
      onTabActivated?.();
    }
  }, [isActive, onTabActivated]);

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadBulkUploadTemplate();
      
      if (response.code === '200' && response.data) {
        bulkUploadUtils.downloadBlob(response.data, 'template_chargement_activite.csv');
        Notifications({
          status: 'success',
          message: 'Template téléchargé avec succès',
          description: 'Le fichier template a été téléchargé.',
          duration: 2,
          placement: 'top',
        });
      } else {
        throw new Error(response.message || 'Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Erreur téléchargement template:', error);
      Notifications({
        status: 'error',
        message: 'Erreur lors du téléchargement du template',
        description: 'Impossible de télécharger le fichier template.',
        duration: 3,
        placement: 'top',
      });
    }
  };

  const handleUpload = async (file: File) => {
    setFileName('');
    setUploadedFile(null);

    const validation = bulkUploadUtils.validateFile(file);
    
    if (!validation.isValid) {
      Notifications({
        status: 'error',
        message: 'Fichier non valide',
        description: validation.error,
        duration: 4,
        placement: 'top',
      });
      return false;
    }

    setFileName(file.name);
    setUploadedFile(file);
    Notifications({
      status: 'success',
      message: 'Fichier chargé avec succès',
      description: `Le fichier "${file.name}" a été chargé.`,
      duration: 2,
      placement: 'top',
    });

    return false; // Empêche l'upload automatique
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      Notifications({
        status: 'error',
        message: 'Votre fichier n\'est pas valide',
        description: 'Aucune limite n\'a été ajoutée !',
        duration: 2,
        placement: 'top',
      });
      return;
    }

    try {
      const response = await processBulkUpload(uploadedFile);

      if (response.code === '200') {
        // Télécharger le fichier de résultat
        bulkUploadUtils.downloadBlob(response.data, 'result_chargement_activite.csv');
        
        Notifications({
          status: 'success',
          message: 'Traitement terminé avec succès',
          description: 'Le fichier de résultat a été téléchargé.',
          duration: 2,
          placement: 'top',
        });
      } else {
        Notifications({
          status: 'error',
          message: response.message || 'Erreur lors du traitement',
          description: response.description || 'Une erreur est survenue.',
          duration: 2,
          placement: 'top',
        });
      }

      // Reset des champs
      setUploadedFile(null);
      setFileName('');
      uploadForm.resetFields();

    } catch (error) {
      console.error('Erreur API:', error);
      Notifications({
        status: 'error',
        message: 'Échec du traitement',
        description: 'Erreur de validation des colonnes CSV. Vérifiez que votre fichier respecte exactement le format du template téléchargé.',
        duration: 6,
        placement: 'top',
      });
    }
  };

  return (
    <DF.Provider pagcode="productlist">
      <div className="bg-color-gray">
        <HeroTitle
          form={form}
          color={color || 'purple'}
          title="Chargement en masse des limites"
          showFilter={false}
          display
          className="productlist"
          noTitleOverlay
          itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
        />
        <Form form={uploadForm}>
          <div className="container">
            <Row className="mb-4 ut-wizard-form" gutter={24} justify="center" align="middle">
              <Col span={22} className="globalvision">
                <div 
                  className="mb-8 staticNotf mt-8 blue"
                  style={{
                    backgroundColor: '#435FCB14',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #d9d9d9'
                  }}
                >
                  <span>
                    Vous pouvez modifier en <strong>masse</strong> les limites des tiers. Pour cela, téléchargez au préalable{' '}
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadTemplate();
                      }}
                      style={{ 
                        color: '#1890ff',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      le fichier template (disponible ici)
                    </a>
                    , complétez-le dans un logiciel tableur, puis uploadez-le sur cette page. 
                    Une vérification vous confirmera si le fichier est valide et si vos données ont bien été prises en compte.
                  </span>
                </div>
                <Form.Item
                  name="upload"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  extra=""
                >
                  <Row gutter={16} align="middle" justify="space-between">
                    <Col span={20}>
                      <Row>
                        <Col>
                          <Upload
                            accept=".csv,.xlsx,.xls"
                            showUploadList={false}
                            beforeUpload={handleUpload}
                            maxCount={1}
                          >
                            <ButtonLink
                              icon={<UploadOutlined color="orange" />}
                              style={{ width: '150px' }}
                              comcode={'upload'}
                              className="ut-reset-btn ut-btn-bordred"
                            />
                          </Upload>
                        </Col>
                        <Col span={19}>
                          <Input
                            style={{ marginLeft: '-25px', width: '100%' }}
                            value={fileName}
                            placeholder="Aucun fichier chargé"
                            readOnly
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <ButtonLink
                        icon={<CheckOutlined />}
                        style={{ width: '150px' }}
                        comcode={'save'}
                        type="primary"
                        className="ut-reset-btn"
                        onClick={handleSave}
                      />
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </DF.Provider>
  );
};

export default BulkUploadTab;